import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Menu, LogOut, X, ChevronRight, UserCog, MessageSquare, Users, Shield, Settings, Heart, MessageCircleHeart } from "lucide-react";

const TEAL = "#0D9488";

/**
 * Mobile-only slide-out drawer. Opens from a hamburger trigger in the header
 * and offers every navigation destination the desktop secondary-nav + user
 * dropdown provide, grouped for scannability.
 *
 * Desktop keeps its full horizontal menu untouched.
 */
export default function MobileNavDrawer({
  user,
  settings,
  dashboardMenu = [],
  primaryNav = [],
  isStaff = false,
  pendingUsers = 0,
  onLogout,
  onSendFeedback,
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const brand = settings?.brand_color || TEAL;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open navigation menu"
        className="w-10 h-10 flex items-center justify-center border border-slate-200 hover:border-teal-500 text-slate-700 hover:text-teal-700 transition-colors"
        data-testid="mobile-nav-trigger"
      >
        <Menu className="w-5 h-5" strokeWidth={2.2} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-sm p-0 bg-white border-l border-slate-200 flex flex-col"
          data-testid="mobile-nav-drawer"
        >
          {/* Custom header — the ui/sheet component already renders its own
              close button but we want a full-width branded strip. */}
          <SheetHeader className="px-5 py-4 border-b border-slate-200 space-y-0 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2.5">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${brand}1a`, color: brand }}>
                  <span className="font-black text-sm">{(settings?.site_name || "A")[0]}</span>
                </div>
              )}
              <SheetTitle className="text-slate-900 text-base font-bold uppercase tracking-tight text-left">
                {settings?.site_name || "The Amino Exchange"}
              </SheetTitle>
            </div>
          </SheetHeader>

          {/* Account chip */}
          {user && (
            <Link
              to="/profile"
              onClick={close}
              className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 hover:bg-slate-50 transition-colors"
              data-testid="mobile-nav-account"
            >
              <div
                className="w-10 h-10 border flex items-center justify-center font-black text-base rounded-full"
                style={{ borderColor: `${brand}66`, backgroundColor: `${brand}1a`, color: brand }}
              >
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-semibold text-slate-900 truncate">{user?.name || "User"}</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{user?.role}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          )}

          {/* Scrollable link list */}
          <div className="flex-1 overflow-y-auto py-2" data-testid="mobile-nav-links">
            <NavSection label="Dashboard">
              <MobileNavLink to="/dashboard" label="Overview" onClick={close} icon={dashboardMenu[0]?.icon} testid="mobile-nav-dashboard" />
              {dashboardMenu.map((item) => (
                <MobileNavLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  onClick={close}
                  testid={`mobile-${item.testid}`}
                />
              ))}
            </NavSection>

            {primaryNav.length > 0 && (
              <NavSection label="Community & Support">
                {primaryNav.map((item) => (
                  <MobileNavLink
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    onClick={close}
                    testid={`mobile-${item.testid}`}
                  />
                ))}
              </NavSection>
            )}

            <NavSection label="Account">
              <MobileNavLink to="/profile" label="Profile & Settings" icon={UserCog} onClick={close} testid="mobile-menu-profile" />
              <MobileNavLink to="/messages" label="Messages" icon={MessageSquare} onClick={close} testid="mobile-menu-messages" />
            </NavSection>

            {isStaff && (
              <NavSection label="Staff">
                <MobileNavLink
                  to="/admin/users"
                  label={user?.role === "moderator" ? "Moderation · Users" : "Users"}
                  icon={Users}
                  onClick={close}
                  testid="mobile-menu-admin-users"
                  badge={pendingUsers > 0 ? `${pendingUsers} new` : null}
                />
                <MobileNavLink to="/admin/supporters" label="Supporters" icon={Heart} onClick={close} testid="mobile-menu-admin-supporters" />
                {user?.role === "admin" && (
                  <>
                    <MobileNavLink to="/library" label="Manage Library" icon={Shield} onClick={close} testid="mobile-menu-admin-library" />
                    <MobileNavLink to="/admin/theme" label="Theme Studio" icon={Settings} onClick={close} testid="mobile-menu-admin-theme" />
                    <MobileNavLink to="/admin/settings" label="Site Settings" icon={Settings} onClick={close} testid="mobile-menu-admin-settings" />
                  </>
                )}
              </NavSection>
            )}
          </div>

          {/* Send Feedback */}
          {user && onSendFeedback && (
            <button
              type="button"
              onClick={() => { close(); onSendFeedback(); }}
              className="border-t border-slate-200 px-5 py-4 text-left flex items-center gap-3 text-slate-800 hover:bg-slate-50 transition-colors uppercase tracking-widest text-xs font-semibold"
              data-testid="mobile-nav-send-feedback"
            >
              <MessageCircleHeart className="w-4 h-4" style={{ color: brand }} />
              Send Feedback
            </button>
          )}

          {/* Logout */}
          {user && onLogout && (
            <button
              type="button"
              onClick={() => { close(); onLogout(); }}
              className="border-t border-slate-200 px-5 py-4 text-left flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors uppercase tracking-widest text-xs font-semibold"
              data-testid="mobile-nav-logout"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NavSection({ label, children }) {
  return (
    <div className="mb-3">
      <div className="px-5 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 font-mono-num">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function MobileNavLink({ to, label, icon: Icon, onClick, testid, badge = null }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center justify-between gap-3 px-5 py-2.5 transition-colors ${isActive ? "bg-teal-50 border-l-2 border-teal-500 text-teal-800" : "text-slate-800 border-l-2 border-transparent hover:bg-slate-50"}`
      }
      data-testid={testid}
    >
      <span className="flex items-center gap-3 min-w-0 flex-1">
        {Icon && <Icon className="w-4 h-4 shrink-0 text-slate-500" />}
        <span className="text-sm truncate">{label}</span>
      </span>
      {badge && (
        <span className="font-mono-num text-[10px] px-1.5 py-0.5 uppercase tracking-widest bg-teal-500 text-white">{badge}</span>
      )}
    </NavLink>
  );
}
